﻿<template>
    <aside class="cart-summary cart-side">
        <div class="basket-summary cart-summary-card">
            <itinerary_breakdown v-if="show_breakdown" display_open=true></itinerary_breakdown>
            <hr v-if="show_breakdown"/>
            <trip_summary v-if="show_summary"></trip_summary>

            <!-- Status -->
            <div class="conf-status" :class="Status_Class()">
                <span class="conf-status-icon" aria-hidden="true">
                    <svg v-if="isCheckingPaymentStatus" class="ts-spinner" viewBox="0 0 24 24" fill="none" aria-hidden="true"><circle cx="12" cy="12" r="10" stroke="currentColor" stroke-width="3" opacity="0.25" /><path d="M22 12a10 10 0 0 0-10-10" stroke="currentColor" stroke-width="3" stroke-linecap="round" /></svg>
                    <svg v-else-if="Status_Class() == 'is-success'" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><path d="M20 6L9 17l-5-5" /></svg>
                    <svg v-else-if="Status_Class() == 'is-warning'" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><path d="M18 6L6 18M6 6l12 12" /></svg>
                    <svg v-else viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="9" /><path d="M12 7v5l3 2" /></svg>
                </span>
                <h2 class="conf-status-title" aria-live="polite">
                    <template v-if="isCheckingPaymentStatus">Checking payment status…</template>
                    <template v-else>{{ isAgent_Login() ? 'Payment status' : !is_last_payment_success() ? 'Payment unsuccessful' : is_part_paid() ? 'Deposit received' : is_fully_paid() ? 'Fully paid' : 'Checking payment status' }}</template>
                </h2>
            </div>

            <p class="conf-message">
                <template v-if="is_part_paid() && is_last_payment_success()">
                    A partial payment was received but your booking is not yet fully paid. Your booking reference is <strong>#{{ $store.getters.get_Booking?.group_booking_id }}</strong>.
                    To pay your remaining balance, request another payment link or reach out if you have questions:
                </template>
                <template v-else-if="is_fully_paid()">
                    Thank you for your payment. Your booking reference is <strong>#{{ $store.getters.get_Booking?.group_booking_id }}</strong> - please quote this if you need to contact us:
                </template>
                <template v-else>
                    Your payment was unsuccessful or cancelled. You have not been charged. Your booking reference is <strong>#{{ $store.getters.get_Booking?.group_booking_id }}</strong> - please quote this if you contact us.
                    To complete your booking, please try again or get in touch:
                </template>
            </p>

            <!-- Brand contact -->
            <p class="conf-contact">
                <a v-if="GetBrand() == 'HEX'" href="mailto:info@highlandexplorertours.com">info@highlandexplorertours.com</a>
                <a v-else-if="GetBrand() == 'HAG'" href="mailto:info@haggisadventures.com">info@haggisadventures.com</a>
                <a v-else-if="GetBrand() == 'AAT' || GetBrand() == 'aatkings'" href="https://www.aatkings.com/contact-us" target="_blank" rel="noopener noreferrer">aatkings.com/contact-us</a>
                <a v-else-if="GetBrand() == 'SEIT'" href="https://seittours.com/contact-us/" target="_blank" rel="noopener noreferrer">seittours.com/contact-us</a>
            </p>

            <!-- Outstanding balance -->
            <div v-if="has_balance_outstanding()" class="conf-box">
                <h3 class="conf-box-title">{{ is_part_paid() ? 'Remaining balance' : 'Payment summary' }}</h3>
                <div class="sm-row">
                    <span>Total outstanding</span>
                    <span><strong>{{ get_total_balance_outstanding_formatted() }}</strong></span>
                </div>
                <div class="sm-row">
                    <span>Payment due date</span>
                    <span>{{ Balance_Due_Date() }}</span>
                </div>
                <div class="sm-grand conf-due">
                    <span class="sm-grand-label">Amount to pay</span>
                    <p class="sm-grand-amount">{{ get_payment_balance_formatted() }} <small>{{ get_Currency() }}</small></p>
                    <span v-if="get_payment_fees() > 0" class="sm-grand-note">Includes card processing &amp; local taxes (fee: {{ get_payment_fees_formatted() }} {{ get_Currency() }})</span>
                </div>
                <button v-if="is_not_paid() || !is_last_payment_success()" type="button" class="button button-pink-book full cart-cta print:hidden" v-on:click="redirect_to_paymentpage(get_payment_amount())">
                    Pay now
                </button>
            </div>

            <!-- Payments processed (allocated) when allocations exist, otherwise payments received (paid transactions) -->
            <div class="conf-box">
                <template v-if="get_total_price_allocated() > 0">
                    <h3 class="conf-box-title">Payments processed</h3>
                    <span class="conf-box-note">Includes all card processing fees and taxes</span>

                    <p v-if="!has_allocated_payments()" class="conf-empty">No payments processed yet.</p>

                    <template v-else>
                        <div v-for="(payment, index) in get_allocated_payments()" :key="index" class="sm-row is-line">
                            <span>{{ payment.date_made }}</span>
                            <span>{{ payment.amount_formatted }} {{ payment.iso_currency_code }}</span>
                        </div>
                        <div class="sm-row is-total">
                            <span>Total paid</span>
                            <span>{{ get_total_price_allocated_formatted() }} <small>{{ get_Currency() }}</small></span>
                        </div>
                    </template>
                </template>

                <template v-else>
                    <h3 class="conf-box-title">Payments received</h3>
                    <span class="conf-box-note">Includes all card processing fees and taxes</span>

                    <p v-if="!has_paid_payments()" class="conf-empty">No payments received yet.</p>

                    <template v-else>
                        <div v-for="(payment, index) in get_paid_payments()" :key="index" class="sm-row is-line">
                            <span>{{ payment.date_made }}</span>
                            <span>{{ payment.amount_formatted }} {{ payment.iso_currency_code }}</span>
                        </div>
                        <div class="sm-row is-total">
                            <span>Total paid</span>
                            <span>{{ get_total_price_paid_formatted() }} <small>{{ get_Currency() }}</small></span>
                        </div>
                    </template>
                </template>

                <div v-if="!is_fully_paid()" class="conf-refresh print:hidden">
                    <span>It can take a few minutes for payments to be processed.</span>
                    <button type="button" @click="reloadPage">
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M23 4v6h-6M1 20v-6h6" /><path d="M3.5 9a9 9 0 0 1 14.9-3.4L23 10M1 14l4.6 4.4A9 9 0 0 0 20.5 15" /></svg>
                        Refresh
                    </button>
                </div>
            </div>

        </div>
    </aside>
</template>
<script lang="ts" src="./summary.ts"></script>
